/**
 * Presentation logic for the farm staff incentive screens.
 *
 * Farm executives are paid PROGRESSIVELY: each band prices only the sales
 * that fall inside it, so 80 sales pay ₹44,500 rather than 80 × the top
 * rate. That is the opposite of the marketing ladder rendered by
 * incentiveSlabs.ts, where crossing a band re-prices everything below it —
 * which is why these are separate modules rather than one with a flag.
 *
 * farmProgressiveTotal mirrors utils/farm_incentives.progressive_total on the
 * backend. It is duplicated here deliberately: the screen previews what a
 * re-rated ladder would pay BEFORE the manager saves it, and there is no
 * endpoint for "price this hypothetical ladder". The agreed worked examples
 * (80 → ₹44,500, 120 → ₹79,500) are asserted in both codebases, so the two
 * cannot drift apart silently.
 */

export interface FarmSlab {
  slab_id?: string;
  min_count: number;
  max_count: number | null;
  rate: number;
  payout_type?: string;
}

export interface FarmSlabRow {
  /** Stable key for React, derived from the band's own bounds. */
  key: string;
  label: string;
  min_count: number;
  max_count: number | null;
  rate: number;
  /** Sales the band covers, or null when it is open-ended. */
  size: number | null;
  /** What completing the whole band pays, or null when open-ended. */
  bandValue: number | null;
}

/** The API spells "no upper bound" as either null or 0, depending on age. */
export const isOpenEnded = (max: number | null | undefined): boolean =>
  max === null || max === undefined || max === 0;

export const farmBandLabel = (min: number, max: number | null | undefined): string =>
  isOpenEnded(max) ? `${min}+` : `${min}–${max}`;

const ordered = (slabs: FarmSlab[]): FarmSlab[] =>
  [...(slabs || [])].sort((a, b) => (a?.min_count ?? 0) - (b?.min_count ?? 0));

/**
 * Total earned for `n` sales against this ladder.
 *
 * Each band contributes only the sales actually made inside it:
 * 80 = 10×0 + 10×200 + 10×500 + 50×750 = ₹44,500.
 */
export function farmProgressiveTotal(n: number, slabs: FarmSlab[]): number {
  if (!n || n <= 0 || !slabs?.length) return 0;
  let total = 0;
  for (const s of ordered(slabs)) {
    const lo = s?.min_count ?? 0;
    if (n < lo) break;
    const top = isOpenEnded(s?.max_count) ? n : Math.min(n, s.max_count as number);
    total += (top - lo + 1) * (s?.rate || 0);
  }
  return total;
}

/** What one more sale adds right now — the number that motivates the screen. */
export const nextSaleEarns = (sales: number, slabs: FarmSlab[]): number =>
  farmProgressiveTotal((sales || 0) + 1, slabs) - farmProgressiveTotal(sales || 0, slabs);

/** One row per band, ordered, with what each band is worth in full. */
export function farmSlabRows(slabs: FarmSlab[]): FarmSlabRow[] {
  return ordered(slabs).map((s) => {
    const open = isOpenEnded(s?.max_count);
    const size = open ? null : (s.max_count as number) - s.min_count + 1;
    return {
      key: `${s.min_count}-${open ? '' : s.max_count}`,
      label: farmBandLabel(s.min_count, s.max_count),
      min_count: s.min_count,
      max_count: open ? null : (s.max_count as number),
      rate: s?.rate || 0,
      size,
      bandValue: size === null ? null : size * (s?.rate || 0),
    };
  });
}

/**
 * How long a visit lasted, for the visits and approval screens.
 *
 * A dash rather than "0m" when there is no duration: the visitor has been
 * checked in but not out, and "0m" would read as a visit that took no time.
 */
export function visitDurationLabel(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (!hours) return `${mins}m`;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}


/** The `projection_basis` a PROJECTED farm_incentive block carries. */
export interface FarmProjectionBasis {
  sales_before?: number;
  sales_after?: number;
}

/**
 * Which sales of the month this order's farm incentive covers.
 *
 * Mirrors incentiveBasisLabel in marketingOrdersView, but counts SALES — one
 * unit on a paid order is one sale on the farm ladder. It answers "why is it
 * that much": a progressive amount depends entirely on where the order sits
 * in the executive's month.
 */
export const farmBasisLabel = (
  basis?: FarmProjectionBasis | null,
): string | null => {
  if (!basis) return null;
  const { sales_before: before, sales_after: after } = basis;
  if (typeof before !== 'number' || typeof after !== 'number') return null;

  const first = before + 1;
  // after <= before would mean the order covers no sales at all. Nothing
  // truthful to render, so say nothing rather than print a broken range.
  if (after < first) return null;

  return first === after
    ? `sale ${first} this month`
    : `sales ${first}–${after} this month`;
};

/**
 * One row of `farm_executive_incentives` on GET /marketing/orders?visitedOnly=true:
 * a farm executive's visited orders and farm incentive for one month.
 */
export interface FarmExecutiveRow {
  exec_mobile: string;
  exec_name: string;
  month: string;
  orders: number;
  eligible_orders: number;
  not_eligible_orders: number;
  units: number;
  credited_orders: number;
  credited_units: number;
  credited_amount: number;
  pending_orders: number;
  pending_units: number;
  pending_amount: number;
  /** Sales on the month's ladder once everything pending is approved. */
  month_sales: number;
  total_incentive: number;
  slab: string;
  slab_id: string;
  rate: number;
  plan_id: string;
  slabs: FarmSlab[];
}

/**
 * How far the month is from its next band: '10 more sales → 21–30 at ₹500/sale'.
 * Null on the last band or with no ladder.
 */
export function nextFarmBandText(sales: number, slabs: FarmSlab[]): string | null {
  const next = ordered(slabs).find((s) => (s?.min_count ?? 0) > (sales || 0));
  if (!next) return null;
  const more = next.min_count - (sales || 0);
  return `${more} more ${more === 1 ? 'sale' : 'sales'} → ${farmBandLabel(next.min_count, next.max_count)} at ₹${(next.rate || 0).toLocaleString('en-IN')}/sale`;
}

/** A farm incentive plan's period, for the Farm Orders plan picker. */
export interface FarmPlanPeriod {
  plan_id: string;
  name: string;
  from: string;
  to: string;
  slabs: FarmSlab[];
}

/**
 * Plans from one or more GET /farm-incentive-plans responses (one per month),
 * de-duplicated, started by `today`, newest first.
 */
export function farmPlanPeriods(
  plans: { plan_id: string; name: string; status?: string; effective_from: string; effective_to?: string | null; slabs?: FarmSlab[] }[],
  today: string,
): FarmPlanPeriod[] {
  const seen = new Map<string, FarmPlanPeriod>();
  for (const p of plans || []) {
    const from = (p?.effective_from || '').slice(0, 10);
    if (!p?.plan_id || !from || from > today || seen.has(p.plan_id)) continue;
    if (p.status && p.status !== 'ACTIVE') continue;
    seen.set(p.plan_id, {
      plan_id: p.plan_id, name: p.name, from,
      to: (p.effective_to || '').slice(0, 10) || today,
      slabs: p.slabs || [],
    });
  }
  return Array.from(seen.values()).sort((a, b) => b.from.localeCompare(a.from));
}
